import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Card, Grid } from "@mui/material";
import { Checkbox } from "@mui/material";
import { getLocalDate } from "../../../../utils/dateHelper";
import { gridLabels } from "../../../../enums";
import useClinic from "../../../../hooks/useClinic";

export default function SelectAnalysisExternalExamCard({
  exam,
  isSelected,
  selectedExam,
  handleCardSelect,
  isDisabled,
}) {
  const { t } = useTranslation();
  const { clinicTimezone } = useClinic();

  const examTitle = exam?.algorithmType.toLowerCase();
  const examGridType = gridLabels[exam?.gridType];

  const leftSideLabels = [
    { label: "FL", field: "fixationLoss" },
    {
      label: "FN",
      field: "falseNegative",
    },
    { label: "FP", field: "falsePositive" },
  ];

  const rightSideLabels = [
    { label: "PSD", field: "patternStandardDeviation" },
    { label: "MD", field: "meanDeviation" },
    { label: "VFI", field: "visualFieldIndex" },
  ];

  return (
    <Card
      data-testid={`select-analysis-external-card-${exam.id}`}
      sx={{
        minWidth: 340,
        backgroundColor: isSelected ? "#E2772E" : "#1E1E2F",
        color: "#FFF",
        borderRadius: 2,
        padding: 2,
        transition: "background-color 0.3s ease",
        display: "flex",
        flexDirection: "column",
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
      onClick={() => {
        if (!isDisabled) handleCardSelect(exam);
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Checkbox
            disabled={isDisabled}
            checked={isSelected}
            sx={{
              padding: 0,
              color: "#FFF",
              "&.Mui-checked": { color: "#fff" },
              "&.Mui-disabled": { color: "#fff" },
            }}
          />
          <Typography style={{ fontSize: "14px", fontWeight: "bold" }}>
            {t(examTitle)} | {examGridType}
          </Typography>
        </Box>
        <Box
          border="1px solid #fff"
          backgroundColor="#fff"
          p={0.5}
          borderRadius="3px"
        >
          <Typography
            fontWeight="bold"
            transition="color 0.3s ease"
            color={isSelected ? "#E2772E" : "#1E1E2F"}
            variant="body2"
          >
            {t("External")}
          </Typography>
        </Box>
      </Box>

      <Grid container gap={isSelected ? 2 : 0}>
        <Grid item xs={12}>
          <Box
            sx={{
              paddingTop: 1,
              paddingBottom: 0,
            }}
          >
            <Typography variant="body2">
              {t("progress_analysis_card_age")}: {exam.age}
            </Typography>
            <Typography variant="body2">
              {t("progress_analysis_exam_date")}:{" "}
              {getLocalDate(exam.completionDate, clinicTimezone)}
            </Typography>
          </Box>
        </Grid>

        <Grid container item>
          <Grid item xs={6}>
            {isSelected && (
              <Box display="flex" flexDirection="column">
                {leftSideLabels.map(({ label, field }, index) => {
                  const value = selectedExam[field];

                  if (value !== null && !isNaN(value)) {
                    const formattedValue = Number(value) * 100;
                    return (
                      <Typography key={index} variant="body2">
                        {label}: {formattedValue.toFixed(2)}%
                      </Typography>
                    );
                  }
                  return null; // Return null if the value is NaN or null
                })}
              </Box>
            )}
          </Grid>

          <Grid item xs={6}>
            {isSelected && (
              <Box display="flex" flexDirection="column">
                {rightSideLabels.map(({ label, field }, index) => {
                  const value = selectedExam[field];

                  if (value !== null && !isNaN(value)) {
                    const formattedValue =
                      field === "visualFieldIndex"
                        ? Number(value) * 100
                        : Number(value);
                    return (
                      <Typography key={index} variant="body2">
                        {label}: {formattedValue.toFixed(2)}
                        {field === "visualFieldIndex" && "%"}
                      </Typography>
                    );
                  }
                  return null; // Return null if the value is NaN or null
                })}
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}
